/**
 * Money helpers shared by the cycle-level pricing block and every plan option card.
 *
 * Pricing inputs (unit price, discount, prepayment, currency) live on the cycle;
 * only the aligner counts and the priced extras differ per plan, so each plan's
 * total is derived rather than stored.
 */
import type { PlanOption, TreatmentStage } from '../../../../types/admin';
import { manufacturedThisStage, parseAmount, type PlanDraft } from './draft';

export type PlanPricingInputs = {
  readonly unitPrice: number;
  readonly discount: number;
  readonly prepayment: number;
  readonly currency: string;
};

export type PlanTotalBreakdown = {
  readonly alignerQty: number;
  readonly alignerSum: number;
  readonly extrasSum: number;
  readonly discount: number;
  readonly prepayment: number;
  readonly total: number;
};

/** Mirrors server response-only `pricing_breakdown`. */
export type StructuredPlanBreakdown = {
  readonly alignersTotal: number;
  readonly additionalPaidTotal: number;
  readonly discountsTotal: number;
  readonly deposit: number;
  readonly amountDue: number;
  readonly manufacturedThisStage: number;
};

const DEFAULT_CURRENCY = 'UAH';

function toFiniteNumber(value: string | null | undefined): number {
  if (value === null || value === undefined || value.trim() === '') return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Ukrainian shorthand for UAH, the raw code for anything else. */
export function currencySuffix(currency: string): string {
  return currency.trim().toUpperCase() === DEFAULT_CURRENCY ? 'грн' : currency.trim();
}

/** Space-grouped amount with a Ukrainian suffix for UAH, the raw code otherwise. */
export function formatMoney(value: number, currency: string): string {
  const rounded = Math.round(value * 100) / 100;
  const hasFraction = Math.abs(rounded - Math.trunc(rounded)) > 0.0001;
  const group = (input: string) => input.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const formatted = hasFraction ? group(rounded.toFixed(2)) : group(Math.trunc(rounded).toString());
  const suffix = currencySuffix(currency);
  return suffix ? `${formatted} ${suffix}` : formatted;
}

/** Reads the cycle-level pricing the plan totals are computed against. */
export function cyclePricingInputs(stage: TreatmentStage): PlanPricingInputs {
  return {
    unitPrice: toFiniteNumber(stage.aligner_unit_price),
    discount: toFiniteNumber(stage.discount_amount),
    prepayment: toFiniteNumber(stage.prepayment_amount),
    currency: stage.pricing_currency?.trim() || DEFAULT_CURRENCY,
  };
}

export function sumAdditionalItems(
  items: readonly { readonly price: string }[] | null | undefined,
): number {
  if (!items) return 0;
  return items.reduce((acc, item) => acc + toFiniteNumber(item.price), 0);
}

/**
 * (upper + lower) x unit price + extras − discount − prepayment.
 *
 * Returns `null` when the cycle has no unit price yet: showing a total of zero
 * would read as "free" rather than "not priced yet".
 */
export function computePlanTotal(
  counts: { readonly upper: number | null; readonly lower: number | null },
  extrasSum: number,
  pricing: PlanPricingInputs,
): PlanTotalBreakdown | null {
  if (pricing.unitPrice <= 0) return null;
  const alignerQty = (counts.upper ?? 0) + (counts.lower ?? 0);
  const alignerSum = alignerQty * pricing.unitPrice;
  return {
    alignerQty,
    alignerSum,
    extrasSum,
    discount: pricing.discount,
    prepayment: pricing.prepayment,
    total: alignerSum + extrasSum - pricing.discount - pricing.prepayment,
  };
}

/** Exact server formula. Whole-course maximum never drives current-stage cost. */
export function computeStructuredPlanTotal(draft: PlanDraft): StructuredPlanBreakdown {
  const manufactured = manufacturedThisStage(draft);
  const alignersTotal =
    draft.alignerPricingMode === 'AUTO'
      ? manufactured * parseAmount(draft.standardUnitPrice)
      : draft.alignerItems.reduce(
          (sum, item) => sum + parseAmount(item.quantity) * parseAmount(item.unitPrice),
          0,
        );
  const additionalPaidTotal =
    (draft.buttons.enabled
      ? parseAmount(draft.buttons.quantity) * parseAmount(draft.buttons.unitPrice)
      : 0) +
    (draft.elastics.enabled
      ? parseAmount(draft.elastics.sets) * parseAmount(draft.elastics.unitPrice)
      : 0) +
    (draft.separation.enabled ? parseAmount(draft.separation.price) : 0) +
    draft.clinicalElements.reduce(
      (sum, element) => sum + (element.paid ? parseAmount(element.price) : 0),
      0,
    );
  const discountsTotal = draft.discounts.reduce(
    (sum, discount) => sum + parseAmount(discount.amount),
    0,
  );
  const deposit = parseAmount(draft.depositAmount);
  return {
    alignersTotal,
    additionalPaidTotal,
    discountsTotal,
    deposit,
    amountDue: alignersTotal + additionalPaidTotal - discountsTotal - deposit,
    manufacturedThisStage: manufactured,
  };
}

/** Ukrainian pluralisation is handled by the backend; the CRM only needs the label stem. */
export function planDisplayTitle(
  option: PlanOption,
  alternativeIndex: number,
  labels: { readonly main: string; readonly alternative: (index: number) => string },
): string {
  if (option.title && option.title.trim()) return option.title.trim();
  return option.is_main ? labels.main : labels.alternative(alternativeIndex);
}

/** Main plan first, then alternatives in `sort_order`, ties broken by id for stability. */
export function sortPlanOptions(options: readonly PlanOption[]): readonly PlanOption[] {
  return [...options].sort((a, b) => {
    if (a.is_main !== b.is_main) return a.is_main ? -1 : 1;
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
    return a.id.localeCompare(b.id);
  });
}

export function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Display title per option id, with the alternatives numbered in display order.
 *
 * The numbering only makes sense over the whole list, so the panel header and
 * the plan rail derive their labels from one shared pass.
 */
export function planTitleMap(
  options: readonly PlanOption[],
  labels: { readonly main: string; readonly alternative: (index: number) => string },
): ReadonlyMap<string, string> {
  const titles = new Map<string, string>();
  let alternativeIndex = 0;
  sortPlanOptions(options).forEach((option) => {
    if (!option.is_main) alternativeIndex += 1;
    titles.set(option.id, planDisplayTitle(option, alternativeIndex, labels));
  });
  return titles;
}

/** How many of an option's four plan meshes finished converting. */
export function readyMeshCount(option: PlanOption): number {
  return (option.meshes ?? []).filter((mesh) => mesh.processing_status === 'ready').length;
}
